import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TomeButton } from '../../primitives/tome';
import {
  ACTION_METADATA,
  groupKeybindingsByAction,
} from '../Settings/keybindingGroups';
import { ActionCategoryFilter, type KeyBindingsRailItem } from '../Settings/components/ActionCategoryFilter';
import { KeybindingTable } from '../Settings/components/KeybindingTable';
import { KeyboardVisualizer } from '../Settings/components/KeyboardVisualizer';
import { ControllerVisualizer } from '../Settings/components/ControllerVisualizer';
import { ControllerBindingsPanel } from './ControllerBindingsPanel';
import {
  keyBindingsNavigationStore,
  useKeyBindingsOrigin,
} from './keyBindingsNavigationStore';
import { useKeyBindingsCapture } from './useKeyBindingsCapture';
import { quickKeybindStore } from '../../Systems/ActionBar/quickKeybindStore';
import './keyBindingsWindow.css';

function detectTouchOrCompactNotice(): boolean {
  if (typeof window === 'undefined') return false;
  const coarse = typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
  const compactPhoneLandscape = window.innerHeight > 0
    && window.innerHeight <= 450
    && window.innerWidth <= 900;
  return coarse || compactPhoneLandscape || localStorage.getItem('helix_touch_controls') === 'on';
}

export type KeyBindingsRailCategory =
  | 'all'
  | 'movement'
  | 'combat'
  | `bar:${number}`
  | 'ui'
  | 'targeting'
  | 'misc'
  | 'event';

export interface KeyBindingsWindowProps {
  /** Optional override of active bar IDs (used by unit tests). */
  activeBarIds?: readonly number[];
  /** Optional initial category in the left rail (defaults to 'all'). */
  defaultCategory?: KeyBindingsRailCategory;
}

export const KeyBindingsWindow: React.FC<KeyBindingsWindowProps> = ({
  activeBarIds: activeBarIdsProp,
  defaultCategory = 'all',
}) => {
  const origin = useKeyBindingsOrigin();
  const [selectedCategory, setSelectedCategory] = useState<KeyBindingsRailCategory>(defaultCategory);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'keyboard' | 'gamepad'>('keyboard');
  const [highlightedAction, setHighlightedAction] = useState<string | null>(null);
  const [confirmingResetAll, setConfirmingResetAll] = useState(false);
  const [showTouchNotice, setShowTouchNotice] = useState<boolean>(() => detectTouchOrCompactNotice());

  const {
    bindings,
    liveBarIds,
    capturingTarget,
    captureError,
    handleBeginCapture,
    handleClearSlot,
    handleResetAction,
    handleConfirmResetAll,
  } = useKeyBindingsCapture(confirmingResetAll, setConfirmingResetAll);

  const activeBarIds = activeBarIdsProp ?? liveBarIds;

  useEffect(() => {
    quickKeybindStore.setTableHoveredAction(capturingTarget?.action ?? highlightedAction);
  }, [highlightedAction, capturingTarget]);

  useEffect(() => {
    return () => {
      quickKeybindStore.setTableHoveredAction(null);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setShowTouchNotice(detectTouchOrCompactNotice());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const allRows = useMemo(
    () => groupKeybindingsByAction(bindings, activeBarIds),
    [bindings, activeBarIds],
  );

  const railCategories = useMemo<KeyBindingsRailItem[]>(() => [
    {
      id: 'all',
      label: 'All Bindings',
      testId: 'keybindings-cat-all',
      count: allRows.length,
    },
    {
      id: 'movement',
      label: 'Movement',
      testId: 'keybindings-cat-movement',
      count: allRows.filter((r) => r.category === 'movement').length,
    },
    {
      id: 'combat',
      label: 'Combat',
      testId: 'keybindings-cat-combat',
      count: allRows.filter((r) => r.category === 'combat' && r.barId === undefined).length,
    },
    ...activeBarIds.map((barId) => ({
      id: `bar:${barId}` as KeyBindingsRailCategory,
      label: `Action Bar ${barId}`,
      testId: `keybindings-cat-bar-${barId}`,
      count: allRows.filter((r) => r.barId === barId).length,
      barId,
    })),
    {
      id: 'ui',
      label: 'Windows',
      testId: 'keybindings-cat-ui',
      count: allRows.filter((r) => r.category === 'ui').length,
    },
    {
      id: 'targeting',
      label: 'Targeting',
      testId: 'keybindings-cat-targeting',
      count: allRows.filter((r) => r.category === 'targeting').length,
    },
    {
      id: 'misc',
      label: 'Interaction',
      testId: 'keybindings-cat-misc',
      count: allRows.filter((r) => r.category === 'misc').length,
    },
    {
      id: 'event',
      label: 'Events',
      testId: 'keybindings-cat-event',
      count: allRows.filter((r) => r.category === 'event').length,
    },
  ], [allRows, activeBarIds]);

  const searchTrimmed = searchQuery.trim().toLowerCase();
  const isSearchActive = searchTrimmed.length > 0;

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      if (isSearchActive) {
        const matchName = row.displayName.toLowerCase().includes(searchTrimmed);
        const matchAction = row.action.toLowerCase().includes(searchTrimmed);
        const matchPrimary = row.primary
          ? `${row.primary.modifier ? `${row.primary.modifier}+` : ''}${row.primary.key}`
            .toLowerCase()
            .includes(searchTrimmed)
          : false;
        const matchSecondary = row.secondary
          ? `${row.secondary.modifier ? `${row.secondary.modifier}+` : ''}${row.secondary.key}`
            .toLowerCase()
            .includes(searchTrimmed)
          : false;
        return matchName || matchAction || matchPrimary || matchSecondary;
      }

      if (selectedCategory === 'all') return true;
      if (selectedCategory.startsWith('bar:')) {
        const barId = Number(selectedCategory.slice(4));
        return row.barId === barId;
      }
      if (selectedCategory === 'combat') {
        return row.category === 'combat' && row.barId === undefined;
      }
      return row.category === selectedCategory;
    });
  }, [allRows, isSearchActive, searchTrimmed, selectedCategory]);

  const focusedBarId = selectedCategory.startsWith('bar:')
    ? Number(selectedCategory.slice(4))
    : null;

  const handleSelectKeycapAction = useCallback((action: string) => {
    setHighlightedAction(action);
    const meta = ACTION_METADATA[action];
    if (!meta) return;
    setSelectedCategory((prev) => {
      if (prev === 'all') return prev;
      if (meta.barId !== undefined) return `bar:${meta.barId}`;
      return meta.category;
    });
  }, []);

  return (
    <div className="key-bindings-window" data-testid="key-bindings-window">
      <div className="key-bindings-toolbar">
        {origin === 'gameMenu' && (
          <button
            type="button"
            className="key-bindings-back-btn"
            data-testid="key-bindings-back-to-menu"
            onClick={() => keyBindingsNavigationStore.returnToGameMenu()}
          >
            ← Game menu
          </button>
        )}

        <div className="key-bindings-search-wrap">
          <input
            type="text"
            className="key-bindings-search-input"
            data-testid="key-bindings-search"
            placeholder="Search by action or key (e.g. Jump, Shift+1)..."
            aria-label="Search keybindings"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="key-bindings-search-clear"
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
            >
              ×
            </button>
          )}
        </div>

        <div className="key-bindings-mode-toggle" role="group" aria-label="Input visualization mode">
          <button
            type="button"
            data-testid="keybindings-mode-keyboard"
            onClick={() => setViewMode('keyboard')}
            className={`key-bindings-mode-btn${viewMode === 'keyboard' ? ' key-bindings-mode-btn--active' : ''}`}
          >
            ⌨️ Keyboard
          </button>
          <button
            type="button"
            data-testid="keybindings-mode-gamepad"
            onClick={() => setViewMode('gamepad')}
            className={`key-bindings-mode-btn${viewMode === 'gamepad' ? ' key-bindings-mode-btn--active' : ''}`}
          >
            🎮 Gamepad
          </button>
        </div>
      </div>

      {showTouchNotice && (
        <div className="key-bindings-touch-notice" data-testid="key-bindings-touch-notice" role="note">
          <span>⌨️</span>
          <span>Keyboard bindings require a physical keyboard or controller.</span>
        </div>
      )}

      <div className="key-bindings-body">
        <ActionCategoryFilter
          categories={railCategories}
          selectedCategory={selectedCategory}
          isSearchActive={isSearchActive}
          onSelectCategory={(catId) => {
            setSelectedCategory(catId);
            if (searchQuery) setSearchQuery('');
          }}
        />

        <div className="key-bindings-table-pane">
          <KeybindingTable
            rows={filteredRows}
            capturingTarget={capturingTarget}
            onBeginCapture={handleBeginCapture}
            onClearSlot={handleClearSlot}
            onResetAction={handleResetAction}
            onHoverAction={setHighlightedAction}
            highlightedAction={highlightedAction}
            captureError={captureError}
            searchActive={isSearchActive}
            forceExpandBars={focusedBarId !== null}
          />
        </div>

        <div className="key-bindings-visualizer-pane">
          {viewMode === 'keyboard' ? (
            <KeyboardVisualizer
              bindings={bindings}
              highlightedAction={highlightedAction}
              onSelectAction={handleSelectKeycapAction}
              activeBarIds={activeBarIds}
              focusedBarId={focusedBarId}
              compact
            />
          ) : (
            <>
              <ControllerBindingsPanel />
              <ControllerVisualizer />
            </>
          )}
        </div>
      </div>

      <div className="key-bindings-footer">
        <span className="key-bindings-footer-hint">
          Click any slot to bind a key or Shift / Ctrl / Alt chord. Press Esc to cancel.
        </span>

        {!confirmingResetAll ? (
          <TomeButton
            tone="secondary"
            data-testid="reset-all-bindings-btn"
            onClick={() => setConfirmingResetAll(true)}
          >
            Reset all to defaults
          </TomeButton>
        ) : (
          <div className="key-bindings-reset-confirm" data-testid="reset-all-confirm-bar" role="alertdialog" aria-label="Confirm reset all key bindings">
            <span className="key-bindings-reset-confirm-text">
              Reset all key bindings to defaults?
            </span>
            <TomeButton
              tone="primary"
              data-testid="confirm-reset-all-bindings-btn"
              onClick={handleConfirmResetAll}
            >
              Confirm Reset
            </TomeButton>
            <TomeButton
              tone="secondary"
              data-testid="cancel-reset-all-bindings-btn"
              onClick={() => setConfirmingResetAll(false)}
            >
              Cancel
            </TomeButton>
          </div>
        )}
      </div>
    </div>
  );
};
